'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useSidebar } from '@/app/context/SidebarContext';
import { PRESS } from '@/app/components/ui/actionButtons';
import { OPEN_COOKIE_SETTINGS_EVENT, watchGoogleConsentMessage } from '@/app/lib/cookieSettings';

const COOKIE_CONSENT_KEY = 'mcworldtools_cookie_consent';

/**
 * Routes that render a full-screen `fixed inset-0 z-50` panel. The banner sits at z-40, so on these
 * it would be invisible BEHIND the page yet still focusable — a keyboard user could tab into an
 * "Accept All" they cannot see. The choice is simply deferred to the next page.
 */
const FULLSCREEN_ROUTES = ['/login', '/signup', '/reset-password'];

// Helper to call gtag safely
function updateGtagConsent(granted: boolean) {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gtag = (window as any).gtag;
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        'analytics_storage': granted ? 'granted' : 'denied'
      });
    }
  }
}

/**
 * Withdrawing analytics consent must also remove what consent allowed: GA's `_ga` / `_ga_<id>`
 * cookies would otherwise sit there for up to two years. They are first-party cookies on the
 * registrable domain (".mcworldtools.com"), so each is expired on the bare host and on every
 * parent domain — whichever one GA used wins, the rest are no-ops.
 */
function clearAnalyticsCookies() {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((n) => n === '_ga' || n.startsWith('_ga_') || n === '_gid');
  if (names.length === 0) return;
  const parts = window.location.hostname.split('.');
  const domains = [''];
  for (let i = 0; i < parts.length - 1; i++) domains.push(`; domain=.${parts.slice(i).join('.')}`);
  for (const name of names) {
    for (const d of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d}`;
    }
  }
}

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  // True while Google's own consent message (UK/EEA/CH) is on screen: ours waits so the two never stack.
  const [googleMessageOpen, setGoogleMessageOpen] = useState(false);
  const { collapsed, mobileOpen } = useSidebar();
  const pathname = usePathname();
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
    }
    // Footer "Privacy and cookie settings" reopens the banner so a choice can be changed at any time.
    const reopen = () => setShowBanner(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
    const stopWatching = watchGoogleConsentMessage(setGoogleMessageOpen);
    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
      stopWatching();
    };
  }, []);

  /**
   * Publish the banner's own height as `--cookie-banner-h` so a bottom-anchored control can sit
   * ABOVE it rather than fight it for a z-index (ChangesPanel's "Changes" button does this).
   *
   * 🚨 A fixed control that merely outranks the banner still lands ON the banner's buttons: at 390px
   * the Changes button overlapped "Accept All" by 42x36px, so raising it would have blocked consent
   * instead of download. Measured, not assumed.
   *
   * The height is not a constant — 191px at 390px wide, 97px from 768px up, and 0 while the banner
   * is hidden (the nav drawer is open, a full-screen route, or the choice is already made), so it is
   * observed rather than hard-coded. Cleared on unmount so nothing keeps an offset the banner no
   * longer occupies.
   */
  useEffect(() => {
    const root = document.documentElement;
    const clear = () => root.style.removeProperty('--cookie-banner-h');
    const el = bannerRef.current;
    if (!el) {
      clear();
      return;
    }
    const publish = () => root.style.setProperty('--cookie-banner-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      clear();
    };
  }, [showBanner, mobileOpen, pathname, googleMessageOpen]);

  const acceptAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: true,
      timestamp: new Date().toISOString()
    }));
    updateGtagConsent(true);
    setShowBanner(false);
  };

  const acceptEssential = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    updateGtagConsent(false);
    clearAnalyticsCookies();
    setShowBanner(false);
  };

  if (!showBanner) return null;
  if (FULLSCREEN_ROUTES.includes(pathname)) return null;
  if (googleMessageOpen) return null;

  return (
    // From lg the sidebar is a fixed rail (w-64, or w-16 collapsed); a full-width banner covered its bottom row,
    // Sign In included. Below lg the sidebar is a drawer — the banner steps aside while it is open so the
    // drawer's own footer is reachable. z-40, not z-50: modals are z-50, and at an equal z-index this later
    // element won, hiding e.g. the version picker's Apply button from a first-time visitor on a short window.
    // `cookie-banner` is a stable hook for the harness (tests/changes-panel-controls.spec.ts hit-tests this
    // banner's own buttons against the panels that used to cover them); no stylesheet keys off it.
    <div
      ref={bannerRef}
      data-nosnippet
      role="dialog"
      aria-label="Cookie consent"
      // Sits ON TOP of a bottom AdSense anchor ad (which outranks every z-index we have), so the
      // Reject / Accept buttons are never under an ad. See AdAnchorClearance.
      style={{ bottom: 'var(--ad-anchor-bottom, 0px)' }}
      className={`cookie-banner fixed left-0 right-0 z-40 p-4 bg-zinc-900 border-t border-zinc-700 shadow-lg ${
        collapsed ? 'lg:left-16' : 'lg:left-64'
      } ${mobileOpen ? 'hidden lg:block' : ''}`}
    >
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex-1 text-sm text-zinc-300">
          <p className="mb-1">
            <strong className="text-white">We use cookies</strong> to enhance your experience on MCWorldTools.
          </p>
          <p className="text-zinc-400">
            Essential cookies keep the site working; analytics cookies show us how it is used. Ads are never
            personalised (in the UK, EEA and Switzerland, Google asks separately about ad cookies). Change your
            mind any time with Privacy and cookie settings in the footer. More in our{' '}
            <Link href="/privacy" className="text-emerald-400 hover:underline">
              Privacy Policy
            </Link>.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={acceptEssential}
            className={`px-4 py-2 text-sm text-emerald-950 bg-emerald-600 rounded-lg hover:bg-emerald-700 ${PRESS}`}
          >
            Reject Analytics
          </button>
          <button
            onClick={acceptAll}
            className={`px-4 py-2 text-sm text-emerald-950 bg-emerald-600 rounded-lg hover:bg-emerald-700 ${PRESS}`}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
